/**
 * Options Editor
 * For editing select/multiselect question options
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, GripVertical, AlertCircle } from 'lucide-react';
import type { Question } from '../../utils/api/questionBankApi';

interface OptionsEditorProps {
  options: Question['options'];
  onChange: (options: Question['options']) => void;
  error?: string;
}

export function OptionsEditor({ options = [], onChange, error }: OptionsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const addOption = () => {
    onChange([...(options || []), { value: '', label: '' }]);
  };

  const updateOption = (index: number, field: 'value' | 'label' | 'description', value: string) => {
    const newOptions = [...(options || [])];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onChange(newOptions);
  };

  const removeOption = (index: number) => {
    const newOptions = options?.filter((_, i) => i !== index);
    onChange(newOptions);
  };

  const moveOption = (index: number, direction: 'up' | 'down') => {
    if (!options) return;
    const newOptions = [...options];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOptions.length) return;
    
    [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
    onChange(newOptions);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Options *</Label>
        <p className="text-sm text-muted-foreground">
          Define the available choices for this question
        </p>
        {error && (
          <p className="text-sm text-destructive flex items-center gap-1 mt-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
      </div>

      {options && options.length > 0 && (
        <div className="space-y-2">
          {options.map((option, index) => (
            <div
              key={index}
              className="border rounded-lg p-3 bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start gap-2">
                {/* Drag Handle */}
                <div className="flex flex-col gap-1 pt-2">
                  <button
                    type="button"
                    onClick={() => moveOption(index, 'up')}
                    disabled={index === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                </div>

                {/* Option Fields */}
                <div className="flex-1 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Value (stored)</Label>
                      <Input
                        value={option.value}
                        onChange={(e) => updateOption(index, 'value', e.target.value)}
                        placeholder="option_1"
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Label (displayed)</Label>
                      <Input
                        value={option.label}
                        onChange={(e) => updateOption(index, 'label', e.target.value)}
                        placeholder="Option 1"
                        className="h-8"
                      />
                    </div>
                  </div>

                  {expandedIndex === index && (
                    <div>
                      <Label className="text-xs">Description (optional)</Label>
                      <Textarea
                        value={option.description || ''}
                        onChange={(e) => updateOption(index, 'description', e.target.value)}
                        placeholder="Additional details about this option..."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                    className="h-8"
                  >
                    {expandedIndex === index ? 'Less' : 'More'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOption(index)}
                    className="h-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={addOption} className="w-full">
        <Plus className="w-4 h-4 mr-2" />
        Add Option
      </Button>

      {/* Quick Add */}
      <div className="border-t pt-4">
        <Label className="text-xs">Quick Add (one per line)</Label>
        <Textarea
          placeholder="Option 1&#10;Option 2&#10;Option 3"
          rows={3}
          onBlur={(e) => {
            const lines = e.target.value.split('\n').filter(Boolean);
            if (lines.length > 0) {
              const newOptions = lines.map(line => ({
                value: line.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                label: line,
              }));
              onChange([...(options || []), ...newOptions]);
              e.target.value = '';
            }
          }}
        />
      </div>
    </div>
  );
}
